import {ServiceBase} from "../ServiceBase";
import {
    GamblingSessionData,
    GamblingSessionManagerErrorCodes,
    IGamblingSessionService
} from "./gambling-session.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";

import {makeObservable, observable, runInAction} from "mobx";
import {GameStepsExecutionResultEnum} from "../game-runner/interfaces/game-step/game-run-step.interface";
import {GamblingSessionSetupComponent} from "../../components/gambling-session/setup/gambling-session-setup.component";
import {DialogButtonClickHandleResultEnum, IModalDialogHandler} from "../dialog/dialog.service.interface";

import {
    GamblingSessionStatus,
    IGamblingSessionUpdateData
} from "../../../sdk/src/services/player_messages_service/firebase/handlers/gambling-session_handler_interfaces";
import {TimeSpan} from "@sparkware/app-utils";
import {CGPEventSubscription} from "../sdk/cgp-sdk.interface";
import {Nullable, NullableString} from "../../../common/types/nullable-types";
import {
    GamblingSessionActionType,
    IGamblingSessionActionData
} from "../../../sdk/src/services/player_messages_service/firebase/handlers/messages_handler_interfaces";
import {
    GamblingSessionLimitReachedComponent,
    LimitType
} from "../../components/gambling-session/limit-reached/gambling-session-limit-reached.component";
import {IToastHandler} from "../toaster/toaster.service.interface";
import {GamblingSessionAnalytics} from "./gambling-session-analytics";
import {IGamblingSessionLimitsSetup} from "./gambling-session-limits-setup.interface";
import {DISPLAY_MESSAGE_IDS} from "../../../common/const/casinofied-analytics-consts";
import {GameInfo} from "../games/game-info";


export class GamblingSessionService extends ServiceBase implements IGamblingSessionService {

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this._gamblingSessionStatePromise = this._createInitialGamblingSessionStatePromise();
        this.analytics = new GamblingSessionAnalytics(services);

        makeObservable(this);

        if (this.isEnabled) {
            this.services.sdk.events.onGamblingSessionAction(this._onGamblingSessionActionHandler);
            this.reaction(() => this.services.user.isAuthorized,
                (isAuthorized) => {
                    if (!isAuthorized) {

                        this._onGamblingSessionUpdateSubscription?.off();
                        if (this._initialPromiseResolver) {
                            //resolve the current promise in case it was hanging around
                            this._initialPromiseResolver(new GamblingSessionData(this._createDefaultGamblingSessionData()));
                            this._initialPromiseResolver = null;
                        }

                        this._advancedWarningLossLimitToast?.close();
                        this._advancedWarningLossLimitToast = null;
                        this._advancedWarningTimeLimitToast?.close();
                        this._advancedWarningTimeLimitToast = null;
                        this._intervalReminderDialog?.close();
                        this._intervalReminderDialog = null;

                        //when the user logs out we reinitialize the promise so if he login again to start all over fresh
                        this._gamblingSessionStatePromise = this._createInitialGamblingSessionStatePromise();

                    }
                });
        }

    }

    readonly analytics: GamblingSessionAnalytics;

    @observable.ref
    private _gamblingSessionStatePromise: Promise<GamblingSessionData>;
    private _onGamblingSessionUpdateSubscription: Nullable<CGPEventSubscription> = null;

    //we use this to resolve the promise in case of logout so it will not hang forever;
    private _initialPromiseResolver: Nullable<((value: GamblingSessionData | PromiseLike<GamblingSessionData>) => void)> = null;


    private _advancedWarningLossLimitToast: IToastHandler | null = null;
    private _advancedWarningTimeLimitToast: IToastHandler | null = null;
    private _intervalReminderDialog: IModalDialogHandler | null = null;

    get isEnabled(): boolean {
        return this.services.regulation.current.isGamblingSessionEnabled;
    }

    get isInitialized(): boolean {
        //if _initialPromiseResolver is null it means that the initial gambling session promise was resolved, so we got the initial firebase message event;
        return !Boolean(this._initialPromiseResolver);
    }



    private _createInitialGamblingSessionStatePromise(): Promise<GamblingSessionData> {
        if(!this.isEnabled) {
            return Promise.resolve(new GamblingSessionData(this._createDefaultGamblingSessionData()));
        }

        //we initialize this promise even if the user is not logged in to make sure that when he actually login we don't miss the onGamblingSessionUpdate event
        return new Promise<GamblingSessionData>(resolve => {
            this._initialPromiseResolver = resolve;
            //here we are listening for the first onGamblingSessionUpdate event
            this._onGamblingSessionUpdateSubscription = this.services.sdk.events.onGamblingSessionUpdate(sessionData => {
                //once we get it we resolve the current promise
                resolve(new GamblingSessionData(sessionData));

                //unsubscribe from the firebase gambling session update event
                this._onGamblingSessionUpdateSubscription.off();

                //we set _gamblingSessionStatePromiseCleaner to null because now our promise is resolved so no need to clean it when the user logout
                this._initialPromiseResolver = null;

                runInAction(() => {
                    this._gamblingSessionStatePromise = Promise.resolve(new GamblingSessionData(sessionData));
                });

                //Then we start listening for subsequent onGamblingSessionUpdate events to update total bets/wins in the game regulation strip
                //or showing this info in different popups
                this._onGamblingSessionUpdateSubscription = this.services.sdk.events.onGamblingSessionUpdate(sessionData => {
                    runInAction(() => {
                        this._gamblingSessionStatePromise = Promise.resolve(new GamblingSessionData(sessionData));
                    });
                });
            });
        })
    }


    private _createDefaultGamblingSessionData(): IGamblingSessionUpdateData {
        return {
            sessionStatus: GamblingSessionStatus.Inactive,
            sessionTotalBets: 0,
            sessionTotalWins: 0,
            gamblingSessionId: "",
            updateCounter: -1
        };
    }

    private _stopGameAutoplay(): void {
        this.services.gameRunner.currentRunningGame?.gameCommunicationChannel?.stopAutoplay();
    }

    private _onGamblingSessionActionHandler = (data: IGamblingSessionActionData) => {
        switch (data.messageType) {
            case GamblingSessionActionType.AdvancedWarningNetLossLimit:

                this._advancedWarningLossLimitToast?.close();

                this.analytics.reportNetLossAdvancedWarningShown(data);

                this._advancedWarningLossLimitToast = this.services.toaster.showAckStandardToast({
                    contentProps: {
                        title: this.services.translation.translate("ATTENTION"),
                        message: this.services.translation.translate("GAMBLING_SESSION_ADVANCED_WARN_LOSS_LIMIT_TOAST"),
                        button: {
                            text: this.services.translation.translate("OK"),
                            onClick: closeToast => closeToast()
                        }
                    },
                    onToastClosed: () => {
                        this._advancedWarningLossLimitToast = null;
                        this.analytics.reportMessageConfirmed(DISPLAY_MESSAGE_IDS.SDNetLossAdvanceWarning, data);
                    }
                });
                break;
            case GamblingSessionActionType.AdvancedWarningTimeLimit:
                this._advancedWarningTimeLimitToast?.close();

                this.analytics.reportTimeLimitAdvancedWarningShown(data);

                this._advancedWarningTimeLimitToast = this.services.toaster.showAckStandardToast({
                    contentProps: {
                        title: this.services.translation.translate("ATTENTION"),
                        message: this.services.translation.translate("GAMBLING_SESSION_ADVANCED_WARN_TIME_LIMIT_TOAST"),
                        button: {
                            text: this.services.translation.translate("OK"),
                            onClick: closeToast => closeToast()
                        }
                    },
                    onToastClosed: () => {
                        this._advancedWarningTimeLimitToast = null;
                        this.analytics.reportMessageConfirmed(DISPLAY_MESSAGE_IDS.SDTimeLimitAdvanceWarning, data);
                    }
                });
                break;
            case GamblingSessionActionType.TimeLimitReached:
                this._stopGameAutoplay();
                this.analytics.reportTimeLimitReachedShown(data);
                this.services.dialog.showModal(dialogHandler => (
                    <GamblingSessionLimitReachedComponent dialogHandler={dialogHandler}
                                                        limitType={LimitType.Time}
                                                        sessionLimitTimestamp={this._formatUTCDate(new Date(data.messageTimeStamp))}
                                                        sessionDuration={TimeSpan.fromSeconds(data.sessionDurationInSeconds)}
                                                        totalBets={data.sessionTotalBets}
                                                        totalWins={data.sessionTotalWins}/>),
                    {
                        title: this.services.translation.translate("GAMBLING_SESSION_TIME_LIMIT_REACHED_TITLE"),
                        noDefaultContentPadding: true,
                        isHighPriority: true,
                        onClosed: () => {
                            this.analytics.reportMessageConfirmed(DISPLAY_MESSAGE_IDS.SDTimeLimitsReached, data);
                        }
                });
                break;
            case GamblingSessionActionType.LossLimitReached:
                this._stopGameAutoplay();
                this.analytics.reportNetLossLimitReachedShown(data);
                this.services.dialog.showModal(dialogHandler => (
                        <GamblingSessionLimitReachedComponent dialogHandler={dialogHandler}
                                                            limitType={LimitType.Loss}
                                                            sessionLimitTimestamp={this._formatUTCDate(new Date(data.messageTimeStamp))}
                                                            sessionDuration={TimeSpan.fromSeconds(data.sessionDurationInSeconds)}
                                                            totalBets={data.sessionTotalBets}
                                                            totalWins={data.sessionTotalWins}/>),
                    {
                        title: this.services.translation.translate("GAMBLING_SESSION_LOSS_LIMIT_REACHED_TITLE"),
                        noDefaultContentPadding: true,
                        isHighPriority: true,
                        onClosed: () => {
                            this.analytics.reportMessageConfirmed(DISPLAY_MESSAGE_IDS.SDNetLossLimitsReached, data);
                        }
                });
                break;
            case GamblingSessionActionType.TimeReminder:
                this._stopGameAutoplay();
                this._intervalReminderDialog?.close();
                this.analytics.reportRealityCheckShow(data);
                this._intervalReminderDialog = this.services.dialog.showModal(dialogHandler => (
                    <GamblingSessionLimitReachedComponent
                        dialogHandler={dialogHandler}
                        limitType={LimitType.IntervalReminder}
                        sessionLimitTimestamp={this._formatUTCDate(new Date(data.messageTimeStamp))}
                        sessionDuration={TimeSpan.fromSeconds(data.sessionDurationInSeconds)}
                        totalBets={data.sessionTotalBets}
                        totalWins={data.sessionTotalWins}
                    />
                ), {
                    title: this.services.translation.translate("GAMBLING_SESSION_INTERVAL_REMINDER_TITLE"),
                    noDefaultContentPadding: true,
                    isHighPriority: true,
                    onClosed: () => {
                        this.analytics.reportMessageConfirmed(DISPLAY_MESSAGE_IDS.SDRealityCheck, data);
                    }
                });
                break;
        }
    }

    private _formatDate(date: Date): string {
        return this.services.time.formatHistoryRecordDateTime(date, false);
    }

    private _formatUTCDate(date: Date): string {
        return this.services.time.formatHistoryRecordDateTime(new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000), false);
    }

    getGamblingSessionState(): Promise<GamblingSessionData> {
        if(this.isInitialized) {
            return this._gamblingSessionStatePromise;
        }

        return new Promise<GamblingSessionData>((resolve, reject) => {
            let hasTimedOut = false;
            const timeoutRef = setTimeout(() => {
                hasTimedOut = true;
                reject('Firebase gambling session update timed out');
            }, TimeSpan.parse(this.services.configuration.servicesConfigs.GamblingSession.firebaseTimeout).totalMilliseconds);

            this._gamblingSessionStatePromise.then(currentState => {
                if(!hasTimedOut) {
                    clearTimeout(timeoutRef);
                    resolve(currentState);
                }
            }).catch(err => {
                if(!hasTimedOut) {
                    clearTimeout(timeoutRef);
                    reject(err);
                }
            });
        });
    }

    private _showGamblingSessionRestrictedMessage(restrictionExpirationDate: Date): void {
        this.services.dialog.showSimpleTextModal({
            title: this.services.translation.translate('generic_notification_popup_title'),
            text: this.services.translation.translate('GAMBLING_SESSION_SELF_RESTRICTION_MESSAGE', {restrictionExpirationDate: this._formatDate(restrictionExpirationDate)}),
            buttons: [
                {
                    text: this.services.translation.translate('OK'),
                    buttonPreset: 'primary',
                    onClick: async () =>  DialogButtonClickHandleResultEnum.CloseDialogAndContinue
                }
            ]
        });
    }

    async startGamblingSession(gameInfo: GameInfo, isFreePlay: boolean): Promise<GameStepsExecutionResultEnum> {

        const currentState = await this.getGamblingSessionState();
        if(isFreePlay) {
            if(currentState.sessionStatus === GamblingSessionStatus.Expired
                || (currentState.sessionStatus === GamblingSessionStatus.Restricted && currentState.restrictionExpirationDate.getTime() < Date.now())){
                const response = await this.services.api.gamblingSessionController.createGamblingSession();
                switch (response.errorCode) {
                    case GamblingSessionManagerErrorCodes.OK:
                    case GamblingSessionManagerErrorCodes.GamblingSessionAlreadyExist:
                        return GameStepsExecutionResultEnum.Continue;
                    default:
                        this._showGeneralErrorMessage();
                        return GameStepsExecutionResultEnum.Cancel;

                }
            } else if(currentState.sessionStatus === GamblingSessionStatus.Restricted && currentState.restrictionExpirationDate.getTime() > Date.now()) {
                //Theoretically we should never reach here because the game will be stopped by the RestrictionsGameStep and the message will be shown there.
                this._showGamblingSessionRestrictedMessage(currentState.restrictionExpirationDate);
                return GameStepsExecutionResultEnum.Cancel;
            }

            return GameStepsExecutionResultEnum.Continue;
        }

        if(currentState.sessionStatus === GamblingSessionStatus.Active) {
            return GameStepsExecutionResultEnum.Continue;
        } else if(currentState.sessionStatus === GamblingSessionStatus.Restricted && currentState.restrictionExpirationDate.getTime() > Date.now()) {
            //Theoretically we should never reach here because the game will be stopped by the RestrictionsGameStep and the message will be shown there.
            this._showGamblingSessionRestrictedMessage(currentState.restrictionExpirationDate);
            return GameStepsExecutionResultEnum.Cancel;
        } else {
            const correlationId = this.services.tools.getGuid();
            return await this._showGamblingSessionSetupDialog(await this._getLastBetActivity(correlationId), correlationId);
        }
    }

    private async _getLastBetActivity(correlationId: string): Promise<NullableString> {
        try {
            const response = await this.services.api.userController.getLastBetActivity({
                correlationId: correlationId
            });

            if(response.LastBetActivity) {
                return this._formatDate(new Date(response.LastBetActivity));
            }

            return null;
        } catch (err) {
            this.services.logger.error('Failed to get last bet activity', err);
            return null;
        }

    }

    private _showGeneralErrorMessage(): void {
        this.services.dialog.showSimpleTextModal({
            text: this.services.regulation.getGeneralErrorMessage(),
            showEmptyTitle: true,
            buttons: [
                {
                    text: this.services.translation.translate('OK'),
                    buttonPreset: 'primary',
                    onClick: async () => DialogButtonClickHandleResultEnum.CloseDialogAndContinue
                }
            ]
        });
    }

    private _showGamblingSessionSetupDialog(lastBetActivity: NullableString, correlationId: string): Promise<GameStepsExecutionResultEnum> {

        this.analytics.reportSetLimitPopupShown(correlationId);

        return new Promise<GameStepsExecutionResultEnum>((resolve, reject) => {

            const onSetLimits = async (limits: IGamblingSessionLimitsSetup) => {
                try {
                    const response = await this.services.api.gamblingSessionController.startGamblingSession({
                        correlationId: correlationId,
                        lossLimitAmount: limits.lossLimitAmount,
                        sessionDurationLimitInSeconds: limits.sessionDurationLimit.totalSeconds,
                        reminderIntervalInSeconds: limits.reminderInterval.totalSeconds,
                        restrictionPeriodInSeconds: limits.restrictionPeriod?.totalSeconds ?? 0,
                    });

                    this.analytics.reportConfirmSetLimitPopup(correlationId, limits, response.errorCode, response.gamblingSessionId);

                    switch (response.errorCode) {
                        case GamblingSessionManagerErrorCodes.OK:
                            resolve(GameStepsExecutionResultEnum.Continue);
                            break;
                        case GamblingSessionManagerErrorCodes.GamblingSessionAlreadyExist:
                            this.services.dialog.showSimpleTextModal({
                                title: this.services.translation.translate('generic_notification_popup_title'),
                                text: this.services.translation.translate('GAMBLING_SESSION_LIMITS_ALREADY_SET'),
                                onClosed: () => resolve(GameStepsExecutionResultEnum.Continue),
                                hideCloseButton: true,
                                buttons: [
                                    {
                                        text: this.services.translation.translate('OK'),
                                        buttonPreset: "primary",
                                        onClick: async () => DialogButtonClickHandleResultEnum.CloseDialogAndContinue
                                    }
                                ]
                            });
                            break;
                        default:
                            this._showGeneralErrorMessage();
                            resolve(GameStepsExecutionResultEnum.Cancel);
                            break;
                    }
                } catch (err) {
                    this.services.logger.error('Failed to start gambling session', err);
                    reject(err);
                }
            }

            this.services.dialog.showModal(dialog => (<GamblingSessionSetupComponent dialogHandler={dialog}
                                                                                                                   lastBetActivity={lastBetActivity}
                                                                                                                   onSetLimits={onSetLimits}/>), {
                title: this.services.translation.translate('GAMBLING_SESSION_SETUP_TITLE'),
                noDefaultContentPadding: true,
                isHighPriority: true,
                mobileCenterHeader: true,
                onClosedByXButton: () => {
                    this.analytics.reportCancelSetLimitPopup(correlationId);
                    resolve(GameStepsExecutionResultEnum.Cancel)
                }
            });
        });
    }

}